import { compareStrings, nullSafeGet } from './DataAccessUtils';
import { getContactName } from './DataFormatterUtils';

export const WEB_SOCKET_MESSAGE_TYPES = {
	NEW_NOTE: 'new_note',
	NOTE_TYPING: 'note_typing',
	NOTE_TYPING_STOP: 'note_typing_stop',
	// A work-order label was added/removed. Deliberately NOT `new_note`: the notes
	// listener (useNotesWebSocket) only reacts to `new_note`, so this never triggers a
	// chat chime or a redundant notes refetch. The detail page applies the label to the
	// cached work order (updating chips), and the notes page refetches off that label
	// change — see getLabelSyncFromNoteEvent and the notes pages' labels-watch effect.
	WO_LABEL_CHANGE: 'wo_label_change',
};

export const WEB_SOCKET_ACTION_TYPES = {
	JOIN: 'join',
	LEFT: 'left',
	PUBLISH: 'publish',
};

export const NEW_NOTE_TYPES = {
	IMAGE: 'image',
	VIDEO: 'video',
	AUDIO: 'audio',
};

export const WEB_SOCKET_ENTITY_TYPES = {
	WORK_ORDER: 'WORK_ORDER',
	WORK_ORDER_NOTES: 'WORK_ORDER_NOTES',
	WORK_ORDER_INTERNAL_NOTES: 'WORK_ORDER_INTERNAL_NOTES',
	RFP: 'RFP',
	BIDDER_NOTES: 'BIDDER_NOTES',
	WALKTHROUGH: 'WALKTHROUGH',
	WALKTHROUGH_NOTES: 'WALKTHROUGH_NOTES',
};

export interface WebSocketPayloadProps {
	entityIdName: string;
	entityId: number;
	currentUser: any;
	messageType?: string;
	entityType: string;
	newNoteEntityType: string;
}

const getContactFromCurrentUser = (currentUser) => ({
	email: currentUser.email,
	nameGiven: nullSafeGet('nameGiven', currentUser),
	nameFamily: nullSafeGet('nameFamily', currentUser),
	contactType: nullSafeGet('contactType', currentUser),
	photoLink: nullSafeGet('photoLink', currentUser),
});

export const getTypingPayload = (payloadProps: WebSocketPayloadProps) => {
	const { entityIdName, entityId, currentUser, messageType, entityType } = payloadProps;

	const payload = {
		actionType: WEB_SOCKET_ACTION_TYPES.PUBLISH,
		entityType: entityType,
		entityIdSeq: [`${entityId}`],
		data: {
			messageType,
			[entityIdName]: entityId,
			typingBy: getContactFromCurrentUser(currentUser),
		},
	};

	return payload;
};

export const getNewNotePayload = (payloadProps: WebSocketPayloadProps, message, newNoteType) => {
	const { entityIdName, entityId, currentUser, newNoteEntityType } = payloadProps;
	const payload = {
		actionType: WEB_SOCKET_ACTION_TYPES.PUBLISH,
		entityType: newNoteEntityType,
		entityIdSeq: [`${entityId}`],
		data: {
			messageType: WEB_SOCKET_MESSAGE_TYPES.NEW_NOTE,
			entityType: newNoteEntityType,
			[entityIdName]: entityId,
			sentBy: getContactFromCurrentUser(currentUser),
			message,
			newNoteType,
		},
	};

	return payload;
};

// Broadcasts a single work-order label add/remove over the WO room. Carries the label
// so receivers can patch the cached work order's labels (updating the title/row chips)
// without a fetch; the notes feed then refetches off that label change to show the
// persisted "Added/Removed Label" log entry. Uses the WO_LABEL_CHANGE type so the notes
// listener ignores it — no chat chime, no redundant notes refetch. One label per event
// mirrors the per-label log entries the backend persists.
export const getWorkOrderLabelChangePayload = (
	payloadProps: WebSocketPayloadProps,
	label,
	action: 'added' | 'removed'
) => {
	const { entityIdName, entityId, currentUser, newNoteEntityType } = payloadProps;
	return {
		actionType: WEB_SOCKET_ACTION_TYPES.PUBLISH,
		entityType: newNoteEntityType,
		entityIdSeq: [`${entityId}`],
		data: {
			messageType: WEB_SOCKET_MESSAGE_TYPES.WO_LABEL_CHANGE,
			entityType: newNoteEntityType,
			[entityIdName]: entityId,
			sentBy: getContactFromCurrentUser(currentUser),
			[action === 'added' ? 'addedLabel' : 'removedLabel']: label,
		},
	};
};

export const getPageEnterOrExitPayload = (
	entityIds,
	actionType,
	entityType = WEB_SOCKET_ENTITY_TYPES.WORK_ORDER
) => {
	const payload = {
		actionType,
		entityType,
		entityIdSeq: entityIds.map((_) => String(_)),
	};

	return payload;
};

export const getUpdatedTypingDetails = (typingDetails = [], newDetail, timeoutMs = 8000) => {
	const now = Date.now();

	const updated = (() => {
		//Just invoking to clear stale data
		if (!newDetail) {
			return typingDetails;
		}

		const existingIndex = (typingDetails || []).findIndex((item) =>
			compareStrings(item.email, newDetail.email)
		);

		if (existingIndex !== -1) {
			return typingDetails.map((item) =>
				item.email === newDetail.email ? { ...item, ...newDetail, lastUpdated: now } : item
			);
		}

		return [...typingDetails, { ...newDetail, lastUpdated: now }];
	})();

	return updated.filter((item) => now - item.lastUpdated <= timeoutMs);
};

export const getTypingDetailsText = (typingDetails = []) => {
	const conjunction = typingDetails.length > 1 ? 'are' : 'is';
	return typingDetails.length > 0
		? `${typingDetails.map((_) => getContactName(_)).join(', ')} ${conjunction} typing...`
		: null;
};

export const notify = () => {
	const audio = new Audio('/sounds/chime-bright.mp3');
	audio.play();
};

export const notifyGentle = () => {
	const audio = new Audio('/sounds/chime-gentle.mp3');
	audio.play();
};

export const getNewNoteType = (entity) => {
	if (!!entity.photo) {
		return NEW_NOTE_TYPES.IMAGE;
	} else if (!!entity.audio) {
		return NEW_NOTE_TYPES.AUDIO;
	} else if (!!entity.video) {
		return NEW_NOTE_TYPES.VIDEO;
	} else {
		return undefined;
	}
};
